import _ from 'lodash';
import type { AttributeMap, Op } from 'quill/core';
import { Delta } from 'quill/core';
import React from 'react';
import type { EmbedValue } from '../editor/embed/matchEmbed';
import { EmbedMode, embedUrl } from '../editor/embed/matchEmbed';
import MentionBase from '../editor/mentions/MentionBase';
import UserMentionIcon from '../editor/mentions/UserMentionIcon';
import type { Mention } from '../editor/types';
import getEditorMentionUrl from '../editor/utils/getEditorMentionUrl';
import getFirstImageUrl from '../editor/utils/getFirstImageUrl';
import postIcon from '../icons/prefilled/post.svg';
import topicIcon from '../icons/prefilled/topic.svg';
import getExternalBrandLogoProps from '../integrations/getExternalBrandLogoProps';
import includesExtension from '../utils/url/includesExtension';
import ExternalBrandLogo from './ExternalBrandLogo';

type Line = {
  delta: Delta;
  attributes: AttributeMap;
};

type GetMentionIconArgs = {
  type: string;
  id: string;
};

export const getMentionIcon = ({ type, id }: GetMentionIconArgs) => {
  switch (type) {
    case 'post':
      return postIcon;
    case 'topic':
      return topicIcon;
    case 'user': {
      return <UserMentionIcon id={id} />;
    }
    case 'external-post':
    case 'group':
      return null;
    default:
      return <ExternalBrandLogo {...getExternalBrandLogoProps(type)} />;
  }
};

function parseString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function parseNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function parseInsertRecord(
  insert: Op['insert'],
): Record<string, unknown> | undefined {
  return typeof insert !== 'string' ? insert : undefined;
}

type Header = 1 | 2 | 3 | 4 | 5 | 6;
function isValidHeader(value: number): value is Header {
  return value in [1, 2, 3, 4, 5, 6];
}

// Generally we have no other key option
/* eslint-disable react/no-array-index-key */
const previewText = (delta: Delta) => {
  return delta.map((op, i) => {
    if (typeof op.insert === 'string') {
      const { attributes, insert } = op;
      if (attributes) {
        if (attributes.searchHighlight) {
          return (
            <em className="search-highlight" key={i}>
              {insert}
            </em>
          );
        }
        if (attributes.diff) {
          return (
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            <span className={`preview-diff-${attributes.diff}`} key={i}>
              {insert}
            </span>
          );
        }
        if (attributes.bold) {
          return <strong key={i}>{insert}</strong>;
        }
        if (attributes.italic) {
          return <em key={i}>{insert}</em>;
        }
        if (attributes.code) {
          return <code key={i}>{insert}</code>;
        }
      }
      return insert;
    }

    if (op.insert?.mention) {
      const mention = op.insert.mention as Mention;
      const { type, id } = mention;
      const mentionText = parseString(op.attributes?.mentionText);
      const content = parseString(op.attributes?.content);
      return (
        <MentionBase
          key={i}
          type={type}
          icon={getMentionIcon(mention)}
          content={
            mentionText || content || getEditorMentionUrl({ id, type, content })
          }
        />
      );
    }
    if (op.insert?.embed) {
      const embed = op.insert.embed as EmbedValue;
      const url = embedUrl(embed, EmbedMode.FULL);
      if (url) {
        return (
          <MentionBase
            key={i}
            type={embed.type}
            icon={getMentionIcon(embed)}
            content={url}
          />
        );
      }
    }
    return '';
  });
};

/* eslint-enable react/no-array-index-key */
const previewLines = (lines: Line[]) => {
  const components = [];
  for (let i = 0; i < lines.length; i += 1) {
    const currentLine = lines[i];
    if (!currentLine) continue; // eslint-disable-line no-continue

    const { attributes, delta } = currentLine;
    if (delta.ops.length <= 0) continue; // eslint-disable-line no-continue
    const first = _.first(delta.ops);

    const insertRecord = parseInsertRecord(first?.insert);
    const image = getFirstImageUrl(insertRecord?.image);
    const video = parseString(insertRecord?.video);
    const header = parseNumber(attributes.header);

    if (image) {
      components.push(
        <figure key={i} className="preview-image">
          <img
            alt="Preview"
            width={parseNumber(first?.attributes?.width)}
            src={image}
          />
        </figure>,
      );
    } else if (video) {
      if (includesExtension(video, ['mp4', 'mov'])) {
        components.push(
          <div key={i} className="preview-video">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video src={video} />
          </div>,
        );
      } else {
        components.push(
          // eslint-disable-next-line jsx-a11y/iframe-has-title
          <iframe
            className="preview-video"
            key={i}
            src={video}
            frameBorder="0"
          />,
        );
      }
    } else if (header && isValidHeader(header)) {
      const Heading: `h${Header}` = `h${header}`;
      components.push(<Heading key={i}>{previewText(delta)}</Heading>);
    } else if (attributes.blockquote) {
      const blocks = [];

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      while (i < lines.length && lines[i]!.attributes.blockquote) {
        if (blocks.length !== 0)
          blocks.push(<br key={`block-${blocks.length}`} />);

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        blocks.push(previewText(lines[i]!.delta));
        i += 1;
      }
      if (i < lines.length) {
        i -= 1;
      }
      components.push(<blockquote key={i}>{blocks}</blockquote>);
    } else if (attributes.list) {
      const listType = attributes.list;
      const children = [];
      while (i < lines.length && lines[i]?.attributes.list === listType) {
        const { indent } = lines[i]?.attributes ?? {};

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const text = previewText(lines[i]!.delta);
        const indentedLines = [];
        i += 1;

        while (
          i < lines.length &&
          // @ts-expect-error
          lines[i].attributes.indent > (parseNumber(indent) || 0)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          indentedLines.push(lines[i]!);
          i += 1;
        }
        children.push(
          <li key={children.length}>
            {text}
            {indentedLines.length > 0 ? previewLines(indentedLines) : null}
          </li>,
        );
      }
      if (i < lines.length) {
        i -= 1; // We broke out of while because list ended and have advanced i too far
      }
      switch (listType) {
        case 'bullet':
          components.push(<ul key={i}>{children}</ul>);
          break;
        case 'ordered':
          components.push(<ol key={i}>{children}</ol>);
          break;
        case 'checked':
          components.push(
            <ul data-checked key={i}>
              {children}
            </ul>,
          );
          break;
        case 'unchecked':
          components.push(
            <ul data-checked={false} key={i}>
              {children}
            </ul>,
          );
          break;
        default:
          break;
      }
    } else {
      components.push(
        <div className="editor-view-paragraph" key={i}>
          {previewText(delta)}
        </div>,
      );
    }
  }
  return components;
};

const truncate = (content: Delta, numLines: number, charsPerLine: number) => {
  let remaining = numLines;
  const lines: Line[] = [];
  content.eachLine((delta, attributes) => {
    if (remaining <= 0) return false;
    if (delta.ops.length <= 0) return true;
    const first = _.first(delta.ops)?.insert;
    if (attributes.header) {
      remaining -= 2;
    } else if (typeof first !== 'string' && (first?.image || first?.video)) {
      remaining -= 8;
    } else {
      const numWrapped = Math.ceil(delta.length() / charsPerLine);
      if (numWrapped >= remaining) {
        lines.push({
          delta: delta.slice(0, charsPerLine * remaining).insert('…'),
          attributes,
        });
        return false;
      }
      remaining -= numWrapped;
    }
    lines.push({ delta, attributes });
    return true;
  });
  return lines;
};

interface ContentPreviewProps {
  content: Delta;
  numLines?: number;
  charsPerLine?: number;
}

const ContentPreview = ({
  content,
  numLines = 6,
  charsPerLine = 50,
}: ContentPreviewProps) => {
  const delta = new Delta(content);
  const lines = truncate(delta, numLines, charsPerLine);
  const preview = previewLines(lines);
  return preview.length > 0 ? (
    <div className="preview-post">{preview}</div>
  ) : null;
};

export default ContentPreview;
